import type { Video } from '@nelnela/contracts';

/**
 * The Library row's fit bar and due chip (design `3a`) — how much of this episode the learner
 * already has, at a glance.
 *
 * One hue, an opacity ramp: ember at .65 for what is solid, ember at .28 for what is shaky, and
 * a bare 8 % of the text colour for what has not been met. Three saturated colours would read as
 * three unrelated facts and would compete with the thumbnail beside them; one colour fading out
 * reads as one quantity, which is what it is.
 *
 * The bar is 96 × 4. It is not a chart and it has no axis: it answers "roughly how much of this
 * do I know", and any more precision than that would be false — `fit` is computed over the items
 * the resolver happens to have tagged, not over every word in the episode.
 */

export const FIT_BAR_WIDTH = 96;
export const FIT_BAR_HEIGHT = 4;

export interface FitSegment {
  key: 'solid' | 'shaky' | 'unmet';
  /** 0..100, and the three always sum to 100 */
  pct: number;
  background: string;
}

/**
 * The three widths, normalised.
 *
 * The server sends fractions of the video's tagged items, so they should already sum to 1 — but
 * they are three independently-rounded floats, and a bar whose segments sum to 99.7 % leaves a
 * hairline of background showing at the end that looks like a fourth category. Normalising here
 * costs nothing and means the bar is always exactly full.
 *
 * A video with nothing tagged yet (a transcript the resolver has not reached) returns null
 * rather than an empty or all-unmet bar: "we do not know" and "you know none of it" are
 * different, and only one of them is true.
 */
export function fitSegments(fit: Video['fit'] | undefined): FitSegment[] | null {
  if (!fit) return null;
  const solid = clamp01(fit.solid);
  const shaky = clamp01(fit.shaky);
  const unmet = clamp01(fit.unmet);
  const total = solid + shaky + unmet;
  if (total <= 0) return null;
  const pct = (n: number): number => Math.round((n / total) * 1000) / 10;
  const a = pct(solid);
  const b = pct(shaky);
  return [
    { key: 'solid', pct: a, background: 'color-mix(in srgb, var(--nn-accent) 65%, transparent)' },
    { key: 'shaky', pct: b, background: 'color-mix(in srgb, var(--nn-accent) 28%, transparent)' },
    // The remainder rather than a third rounding, so the three always sum to exactly 100.
    {
      key: 'unmet',
      pct: Math.max(0, Math.round((100 - a - b) * 10) / 10),
      background: 'color-mix(in srgb, var(--nn-text) 8%, transparent)',
    },
  ];
}

const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

/**
 * How many words in this episode are due, for the chip beside the bar.
 *
 * Read from `dueItemCount`, not derived from `dueItemDensity`: the density is a fraction over
 * the video's tagged items and the denominator never crosses the wire, so a count computed from
 * it would be an invention. Null when there are none — the chip is a reason to open THIS episode
 * rather than another, and "0 due words" is not one.
 */
export function dueWordCount(video: Pick<Video, 'dueItemCount'>): number | null {
  const n = video.dueItemCount;
  if (n === undefined || !Number.isFinite(n) || n <= 0) return null;
  return Math.round(n);
}

export function FitBar({ fit }: { fit: Video['fit'] | undefined }) {
  const segments = fitSegments(fit);
  if (!segments) return null;
  return (
    <span
      aria-hidden
      style={{
        display: 'flex',
        width: FIT_BAR_WIDTH,
        height: FIT_BAR_HEIGHT,
        borderRadius: FIT_BAR_HEIGHT / 2,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {segments.map((s) => (
        <span key={s.key} style={{ width: `${s.pct}%`, background: s.background }} />
      ))}
    </span>
  );
}

/** "{N} due words" (`3a`). Rendered only when N > 0; see `dueWordCount`. */
export function DueChip({ n }: { n: number }) {
  return (
    <span style={{ fontSize: 10.5, color: 'var(--nn-accent-text)' }}>
      {n} due {n === 1 ? 'word' : 'words'}
    </span>
  );
}
